import Animated, {
  useAnimatedStyle,
  useSharedValue,
  withTiming,
} from 'react-native-reanimated';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createStyles } from '@/contexts/theme-context';
import {
  Dimensions,
  LayoutChangeEvent,
  Modal,
  StyleProp,
  TouchableWithoutFeedback,
  View,
  ViewStyle,
} from 'react-native';
import { shadow } from '@/styles';

type SidePosition = 'top' | 'right' | 'bottom' | 'left';
type LocationPosition = 'start' | 'mid' | 'end';

export type DropdownPosition = `${SidePosition}-${LocationPosition}`;

export interface DropdownProps {
  children: React.ReactNode;
  containerStyle?: StyleProp<ViewStyle>;
  isVisible: boolean;
  onClose: () => void;
  position: DropdownPosition;
  trigger: React.ReactNode;
}

// Position calculation functions for anchor points
const getPositionCalculators = (
  x: number,
  y: number,
  w: number,
  h: number,
) => ({
  'bottom-end': () => ({ x: x + w, y: y + h }),
  'bottom-mid': () => ({ x: x + w / 2, y: y + h }),
  'bottom-start': () => ({ x: x, y: y + h }),
  'left-end': () => ({ x: x, y: y + h }),
  'left-mid': () => ({ x: x, y: y + h / 2 }),
  'left-start': () => ({ x: x, y: y }),
  'right-end': () => ({ x: x + w, y: y + h }),
  'right-mid': () => ({ x: x + w, y: y + h / 2 }),
  'right-start': () => ({ x: x + w, y: y }),
  'top-end': () => ({ x: x + w, y: y }),
  'top-mid': () => ({ x: x + w / 2, y: y }),
  'top-start': () => ({ x: x, y: y }),
});

// This map is used to rotate the dropdown position to find a position that doesn't go off screen
// It is a circular map that rotates clockwise from the current position
// Because the position is relative to the trigger, the positions may appear to be going backwards
// when they are actually going forwards relative to the dropdown
const POSITION_ROTATION_MAP: Record<DropdownPosition, DropdownPosition> = {
  'bottom-end': 'left-start',
  'bottom-mid': 'bottom-end',
  'bottom-start': 'bottom-mid',
  'left-end': 'top-end',
  'left-mid': 'left-end',
  'left-start': 'left-mid',
  'right-end': 'right-mid',
  'right-mid': 'right-start',
  'right-start': 'bottom-start',
  'top-end': 'top-mid',
  'top-mid': 'top-start',
  'top-start': 'right-end',
};

export const Dropdown: React.FC<DropdownProps> = ({
  children,
  containerStyle,
  isVisible,
  onClose,
  position,
  trigger,
}) => {
  const [styles] = useStyles();
  const { width: screenWidth, height: screenHeight } = Dimensions.get('window');
  const triggerRef = useRef<View>(null);
  const [dropdownDimensions, setDropdownDimensions] = useState<{
    height: number;
    width: number;
  } | null>(null);
  const [triggerMeasurements, setTriggerMeasurements] = useState<{
    height: number;
    width: number;
    x: number;
    y: number;
  } | null>(null);
  const scale = useSharedValue(0.84);
  const opacity = useSharedValue(0);

  useEffect(() => {
    if (isVisible) {
      scale.value = withTiming(1, { duration: 167 });
      opacity.value = withTiming(1, { duration: 167 });
    } else {
      scale.value = 0.84;
      opacity.value = 0;
    }
  }, [isVisible, scale, opacity]);

  // Check if dropdown goes off screen for a given position
  const isOffScreen = useCallback(
    (
      testPosition: DropdownPosition,
      dropdownWidth: number,
      dropdownHeight: number,
    ) => {
      if (!triggerMeasurements) {
        return false;
      }

      const { x, y, width, height } = triggerMeasurements;
      const testAnchor = getPositionCalculators(x, y, width, height)[
        testPosition
      ]();

      const { left, top } = calculateOffsetFromAnchor(
        testAnchor,
        testPosition,
        dropdownWidth,
        dropdownHeight,
      );

      return (
        left < 0 ||
        top < 0 ||
        left + dropdownWidth > screenWidth ||
        top + dropdownHeight > screenHeight
      );
    },
    [triggerMeasurements, screenWidth, screenHeight],
  );

  const animatedStyle = useAnimatedStyle(() => {
    return {
      opacity: opacity.value,
      transform: [{ scale: scale.value }],
    };
  }, [scale, opacity]);

  // Skip state writes when the measurements haven't changed to avoid re-render loops
  const handleLayout = (event: LayoutChangeEvent) => {
    const { width, height } = event.nativeEvent.layout;
    setDropdownDimensions((prev) =>
      prev && prev.height === height && prev.width === width
        ? prev
        : { height, width },
    );
    triggerRef.current?.measureInWindow((x, y, w, h) => {
      setTriggerMeasurements((prev) =>
        prev &&
        prev.height === h &&
        prev.width === w &&
        prev.x === x &&
        prev.y === y
          ? prev
          : { height: h, width: w, x, y },
      );
    });
  };

  const calculateOffsetFromAnchor = (
    anchorPoint: { x: number; y: number },
    finalPosition: string,
    dropdownWidth: number,
    dropdownHeight: number,
  ) => {
    let left = anchorPoint.x;
    let top = anchorPoint.y;

    const [side, location] = finalPosition.split('-') as [
      SidePosition,
      LocationPosition,
    ];

    const topAdjustments: Record<LocationPosition, number> = {
      end: dropdownHeight,
      mid: dropdownHeight / 2,
      start: 0,
    };

    const leftAdjustments: Record<LocationPosition, number> = {
      end: dropdownWidth,
      mid: dropdownWidth / 2,
      start: 0,
    };

    switch (side) {
      case 'top':
        top = anchorPoint.y - dropdownHeight;
        left = anchorPoint.x - leftAdjustments[location];
        break;

      case 'bottom':
        top = anchorPoint.y;
        left = anchorPoint.x - leftAdjustments[location];
        break;

      case 'left':
        left = anchorPoint.x - dropdownWidth;
        top = anchorPoint.y - topAdjustments[location];
        break;

      case 'right':
        left = anchorPoint.x;
        top = anchorPoint.y - topAdjustments[location];
        break;
    }

    return { left, top };
  };

  // Calculate dropdown position based on anchor point and position
  const getPositionStyle = useCallback(() => {
    const dropdownWidth = dropdownDimensions?.width;
    const dropdownHeight = dropdownDimensions?.height;

    if (!dropdownWidth || !dropdownHeight) {
      return {
        left: 0,
        position: 'absolute' as const,
        top: 0,
      };
    }

    // Find a position that doesn't go off screen by rotating clockwise
    let finalPosition = position;
    let currentPosition = position;
    const maxAttempts = 12; // Prevent infinite loop
    let attempts = 0;

    while (
      attempts < maxAttempts &&
      isOffScreen(currentPosition, dropdownWidth, dropdownHeight)
    ) {
      currentPosition = POSITION_ROTATION_MAP[currentPosition];
      finalPosition = currentPosition;
      attempts++;
    }

    if (!triggerMeasurements) {
      return {
        left: 0,
        position: 'absolute' as const,
        top: 0,
      };
    }

    const { x, y, width, height } = triggerMeasurements;
    const positions = getPositionCalculators(x, y, width, height);
    const anchorPoint = positions[finalPosition]();

    const { left, top } = calculateOffsetFromAnchor(
      anchorPoint,
      finalPosition,
      dropdownWidth,
      dropdownHeight,
    );

    return {
      left,
      position: 'absolute' as const,
      top,
    };
  }, [
    dropdownDimensions?.width,
    dropdownDimensions?.height,
    position,
    isOffScreen,
    triggerMeasurements,
  ]);

  return (
    <>
      <View ref={triggerRef}>{trigger}</View>
      <Modal
        animationType="none"
        onRequestClose={onClose}
        transparent
        visible={isVisible}
      >
        <TouchableWithoutFeedback onPress={onClose}>
          <View style={styles.overlay}>
            <TouchableWithoutFeedback>
              <Animated.View
                onLayout={handleLayout}
                style={[
                  styles.dropdownContainer,
                  getPositionStyle(),
                  containerStyle,
                  animatedStyle,
                ]}
              >
                {children}
              </Animated.View>
            </TouchableWithoutFeedback>
          </View>
        </TouchableWithoutFeedback>
      </Modal>
    </>
  );
};

const POPOVER_SHADOW = shadow({
  elevation: 6,
  opacity: 0.05,
  radius: 12,
  x: 0,
  y: 0,
});

const useStyles = createStyles((theme) => ({
  dropdownContainer: {
    backgroundColor: theme.sk.color.bg.primary.default,
    borderRadius: theme.sk.radii.radiusMedium,
    ...POPOVER_SHADOW,
  },
  overlay: {
    flex: 1,
  },
}));
